import React from "react";
import { Link } from "react-router-dom";
import { Copy, Lock, MapPin, Heart, MessageCircle, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * LockedFeaturesReel — /share/locked-features
 * Grey-out demo: shows what anonymous visitors CAN'T see, then reveals it.
 */
const CAPTION = `🔒 What you're missing without a CampCompass account…

Real prices · full map · save favourites · Campfire Chat · trip photos · weather alerts.

Sign up FREE (60 secs, no card) — everything unlocks the moment you're in.

👉 camp-compass.uk
#CampCompass #UKCamping #SignUpFree`;

export default function LockedFeaturesReel() {
  const copy = () => navigator.clipboard.writeText(CAPTION).then(() => toast.success("Caption copied"));
  const lockedItems = [
    { icon: MapPin,        label: "Real prices + full map" },
    { icon: Heart,         label: "Save favourites" },
    { icon: MessageCircle, label: "Campfire Chat + DMs" },
    { icon: Camera,        label: "Share your trip photos" },
  ];
  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="locked-features-reel">
      <div className="max-w-[420px] mx-auto p-4 space-y-3">
        <div className="text-center">
          <Link to="/launch-pack" className="text-[11px] mono uppercase tracking-widest text-[#ff4500] hover:underline">← Back to Launch Pack</Link>
          <h1 className="font-display text-2xl mt-1">Reel · Locked Features</h1>
          <p className="text-xs text-[#666]">3 scenes · 9:16</p>
        </div>
        <button onClick={copy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0e0e0e] text-white text-xs font-black uppercase tracking-widest px-4 py-2.5" data-testid="locked-copy">
          <Copy className="w-3 h-3" /> Copy caption
        </button>

        <Scene bg="from-[#0e0e0e] via-[#1a1a1a] to-[#0e0e0e]" testid="locked-scene-1">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 text-[11px] mono uppercase tracking-widest font-black mb-4">
              <Lock className="w-3 h-3" /> Locked
            </div>
            <div className="font-display text-3xl leading-tight mb-6">Without an account you see…</div>
            <ul className="text-left space-y-2 w-full">
              {lockedItems.map((it) => (
                <li key={it.label} className="flex items-center gap-3 bg-white/5 border border-white/10 px-3 py-2">
                  <it.icon className="w-4 h-4 text-white/40" strokeWidth={1.75} />
                  <span className="filter blur-sm text-white/60 text-sm">{it.label}</span>
                  <Lock className="w-3 h-3 text-white/40 ml-auto" />
                </li>
              ))}
            </ul>
          </div>
        </Scene>

        <Scene bg="from-[#ff4500] via-[#e03d00] to-[#a82e00]" testid="locked-scene-2">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="font-display text-5xl leading-none mb-4">Sign up.</div>
            <ul className="text-left space-y-2 w-full">
              {lockedItems.map((it) => (
                <li key={it.label} className="flex items-center gap-3 bg-white/15 px-3 py-2">
                  <it.icon className="w-4 h-4" strokeWidth={2.25} />
                  <span className="text-sm font-bold">{it.label}</span>
                </li>
              ))}
            </ul>
            <p className="text-white/90 text-sm mt-4">All unlocked. Free while we grow.</p>
          </div>
        </Scene>

        <Scene bg="from-[#0a7f3f] via-[#0e8f4a] to-[#083c1e]" testid="locked-scene-3">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="font-display text-4xl leading-tight mb-4">60 seconds.</div>
            <p className="text-white/90 text-sm mb-6">No card. No catch. Free while we grow.</p>
            <div className="bg-white text-[#0a7f3f] font-black uppercase tracking-widest text-xs px-5 py-3">
              camp-compass.uk
            </div>
          </div>
        </Scene>
      </div>
    </div>
  );
}

function Scene({ bg, testid, children }) {
  return (
    <div className={`aspect-[9/16] w-full flex flex-col bg-gradient-to-br ${bg}`} data-testid={testid}>
      {children}
    </div>
  );
}
